import { useState, useEffect } from "react";
import CustomSelectBox from "../../components/Inputs/CustomSelectBox";
import TextAreaInput from "../../components/Inputs/TextAreaInput";
import CustomButton from "../../components/CustomButton";
import { TfiReload } from "react-icons/tfi";
import styles from "./JournalRecommender.module.css";
import { MdOutlineFilterAltOff } from "react-icons/md";
import { FiSearch } from "react-icons/fi";
import { useProject } from "../../contexts/ProjectContextProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  generateAbstractWithAI,
  getRecommendedJournals,
  voteToJournal,
} from "../../api/Api";
import CustomSkeleton from "../../components/CustomSkeleton";
import Pagination from "../../components/ui/Pagination";
import { showToast } from "../../utils/toastUtils";
import VoteBox from "../../components/ui/vote/VoteBox";

// for the journal card component
const JournalCard = ({ journal, onRate }) => {
  console.log("journal", journal);
  const getMatchBadgeStyles = (match) => {
    if (match >= 90) {
      return { backgroundColor: "#63D06E" };
    }
    if (match >= 70) {
      return { backgroundColor: "#FFC107" };
    }
    return { backgroundColor: "#adb0b4" };
  };

  const getMatchBorderStyles = (match) => {
    if (match >= 90) {
      return { border: "1px solid #63D06E", borderRadius: "10px" };
    }
    if (match >= 70) {
      return { border: "1px solid #FFC107", borderRadius: "10px" };
    }
    return {};
  };

  return (
    <div
      key={journal.title + journal.publisher}
      className="card mb-3 position-relative"
      style={{ borderRadius: "10px" }}
    >
      <div
        className="card-body p-4"
        style={getMatchBorderStyles(journal.match_score)}
      >
        <div className="d-flex justify-content-between ">
          <p className="p3 fw-semibold mb-1">{journal.title}</p>
          <div>
            <span
              className={`badge ${styles.rounded}`}
              style={getMatchBadgeStyles(journal.match_score)}
            >
              {journal.match_score}% Match
            </span>
          </div>
        </div>
        <h6 className="mb-2 text-muted p5">{journal.publisher}</h6>
        <p className="p4 text-secondary ">{journal.description}</p>
        <p className="p5 mb-2">
          <span className="p4 fw-semibold ">Impact Factor:</span>{" "}
          {journal.impactFactor}
        </p>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
          <div className="btn-group gap-2 flex-wrap">
            {/* <CustomButton
              variant="outline"
              className="btn-sm px-3 py-2 d-flex align-items-center justify-content-center gap-2"
              onClick={() => console.log("Download Template")}
            >
              <LuDownload /> <span className='fw-normal'>Download Template</span>
            </CustomButton>
            <CustomButton
              variant="outline"
              className="btn-sm px-3 py-2 d-flex align-items-center justify-content-center gap-2"
              onClick={() => console.log("Author Guidelines")}
            >
              <TbExternalLink /> <span className='fw-normal'>Author Guidelines</span>
            </CustomButton> */}
          </div>
          {/* todo: add vote box for the journal. make sure to add the unique_id to the vote box */}
          <VoteBox
            itemId={journal.unique_id}
            feedbackType={journal.feedback} // Optionally track last vote for this journal
            onRate={(feedbackType, itemId, feedbackMessage) =>
              onRate(
                feedbackType,
                itemId,
                feedbackMessage,
                journal.title,
                journal.publisher
              )
            }
            itemType="journal"
          />
        </div>
      </div>
    </div>
  );
};

// Remove the static subjectOptions array
// const subjectOptions = [
//   { value: "", label: "All Subjects" },
//   { value: "medical", label: "Medical Research" },
//   { value: "biology", label: "Biology" },
//   { value: "chemistry", label: "Chemistry" },
//   { value: "physics", label: "Physics" },
//   { value: "multidisciplinary", label: "Multidisciplinary" }
// ];

const sortOptions = [
  { value: "", label: "Default" },
  { value: "match", label: "Match Percentage" },
  { value: "impact", label: "Impact Factor" },
];

// ---------------------------------------------------------------------------------
// JournalRecommender component
const JournalRecommender = () => {
  const { data: projects, getWorkStreams, workStreams } = useProject();

  // Separate state for original data and filtered data
  const [originalJournals, setOriginalJournals] = useState([]);
  const [filteredJournals, setFilteredJournals] = useState([]);

  // pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
  });

  // Calculate paginated journals from filtered results
  const paginatedJournals = filteredJournals.slice(
    (pagination.currentPage - 1) * pagination.itemsPerPage,
    pagination.currentPage * pagination.itemsPerPage
  );

  // Filter states
  const [openAccess, setOpenAccess] = useState(false);
  const [subjectArea, setSubjectArea] = useState("");
  const [sortBy, setSortBy] = useState("");

  const [loading, setLoading] = useState({ abstract: false, journals: false });
  const [apiError, setApiError] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");

  const formik = useFormik({
    initialValues: {
      projectId: "",
      workStreamId: "",
      isConnectChecked: true,
      abstract: "",
    },
    validationSchema: Yup.object({
      projectId: Yup.string().when("isConnectChecked", {
        is: (val) => val === true,
        then: () => Yup.string().required("Project is required"),
        otherwise: () => Yup.string().notRequired(),
      }),
      workStreamId: Yup.string().when("isConnectChecked", {
        is: (val) => val === true,
        then: () => Yup.string().required("Work Stream is required"),
        otherwise: () => Yup.string().notRequired(),
      }),
      abstract: Yup.string().notRequired(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setLoading((prev) => ({ ...prev, abstract: true }));
        setApiError(null);

        if (values.isConnectChecked) {
          const response = await generateAbstractWithAI({
            projectId: values.projectId,
            workStreamId: values.workStreamId,
          });

          if (response?.status === 200) {
            formik.setFieldValue("abstract", response.data?.abstract);
            setAiPrompt(response.data?.abstract);
          } else {
            throw new Error("Failed to generate abstract");
          }
        } else {
          // If not using AI generation, set the aiPrompt to the manually entered abstract
          setAiPrompt(values.abstract);
        }

        console.log("Finding matching journals for:", values.abstract);
      } catch (error) {
        console.error("Error:", error);
        setApiError(
          error.message || "An error occurred while processing your request"
        );
      } finally {
        setLoading((prev) => ({ ...prev, abstract: false }));
        setSubmitting(false);
      }
    },
  });

  const handleConnectToggle = () => {
    formik.setFieldValue("isConnectChecked", !formik.values.isConnectChecked);
    if (!formik.values.isConnectChecked) {
      formik.setFieldValue("projectId", "");
      formik.setFieldValue("workStreamId", "");
    }
  };

  // Apply filters and sorting whenever filter criteria or original data changes
  useEffect(() => {
    let filtered = [...originalJournals];

    // Filter by open access
    if (openAccess) {
      filtered = filtered.filter((journal) => journal.isOpenAccess);
    }

    // Filter by subject area
    if (subjectArea) {
      filtered = filtered.filter(
        (journal) => journal.subjectArea === subjectArea
      );
    }

    // Sort journals based on selected criteria
    switch (sortBy) {
      case "impact":
        filtered.sort((a, b) => b.impactFactor - a.impactFactor);
        break;
      case "match":
      default:
        filtered.sort((a, b) => b.match_score - a.match_score);
        break;
    }

    setFilteredJournals(filtered);

    // Reset pagination to first page when filters change
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [originalJournals, openAccess, subjectArea, sortBy]);

  // handle rate the journal
  const handleRate = async (
    ratingType,
    journalId,
    feedbackMessage,
    journalName,
    journalPublisher
  ) => {
    console.log("ratingType", ratingType);
    console.log("journalId", journalId);
    console.log("feedbackMessage", feedbackMessage);
    try {
      // Handle vote submission
      const vote =
        ratingType === "positive"
          ? true
          : ratingType === "negative"
          ? false
          : null;

      // Update the journal's feedback state
      setOriginalJournals((prev) =>
        prev.map((j) =>
          j.title === journalName && j.publisher === journalPublisher
            ? { ...j, feedback: ratingType }
            : j
        )
      );

      // vote to the journal with the feedback message
      const response = await voteToJournal(journalId, {
        vote,
        feedback: feedbackMessage,
        journalName,
        journalPublisher,
        aiPrompt: aiPrompt || formik.values.abstract,
      });
      if (response?.status === 200) {
        if (vote) showToast("Thank you for your feedback!", "success");
      } else {
        showToast("Failed to submit vote", "error");
      }
    } catch (error) {
      showToast("Failed to submit vote", "error");
      console.error("Voting failed", error);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setOpenAccess(false);
    setSubjectArea("");
    setSortBy("");
  };

  const getJournalRecommendations = async () => {
    if (!formik.values.abstract) {
      setApiError("Please enter an abstract to find matching journals.");
      return;
    }
    try {
      setLoading((prev) => ({ ...prev, journals: true }));
      setApiError(null);

      // Set the aiPrompt to the current abstract
      setAiPrompt(formik.values.abstract);

      const response = await getRecommendedJournals({
        abstract: formik.values.abstract,
      });

      if (response?.status === 200) {
        console.log("Journal recommendations:", response.data);
        // Set original journals data
        setOriginalJournals(response.data || []);
        // Reset pagination when new data is loaded
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      } else {
        setApiError(
          response.message || "Failed to fetch journal recommendations"
        );
      }
    } catch (error) {
      console.error("Error fetching journal recommendations:", error);
      setApiError(
        error.message ||
          "An error occurred while fetching journal recommendations"
      );
    } finally {
      setLoading((prev) => ({ ...prev, journals: false }));
    }
  };

  // debuging logs
  // console.log('Original journals:', originalJournals.length);
  // console.log('Filtered journals:', filteredJournals.length);
  // console.log('Paginated journals:', paginatedJournals.length);

  // Generate subject options dynamically from originalJournals
  const subjectOptions = [
    { value: "", label: "All Subjects" },
    ...Array.from(
      new Set(originalJournals.map((j) => j.subjectArea).filter(Boolean))
    ).map((area) => ({ value: area, label: area })),
  ];

  return (
    <div className="container-fluid p-4" style={{ backgroundColor: "#F9FAFC" }}>
      <form onSubmit={formik.handleSubmit} className="container">
        <header className="mb-4">
          <span className=" h5 fw-bold">Journal Recommender</span>
          <p className="text-muted p4 m-0">
            Discover the best-fit journals for your research.
          </p>
          <p className="text-muted p4 ">
            Generate or paste your abstract below to get tailored journal
            recommendations.
          </p>
        </header>

        <div className="container bg-white p-4 rounded">
          <div className="mb-4 d-flex align-items-start gap-2">
            <input
              type="checkbox"
              id="connect"
              className={`form-check-input mt-1 me-2 ${styles.roundCheckbox}`}
              checked={formik.values.isConnectChecked}
              onChange={handleConnectToggle}
            />
            <label htmlFor="connect" className="form-check-label text-sm">
              Connect an existing project to generate an abstract with AI
            </label>
          </div>

          {formik.values.isConnectChecked && (
            <>
              <div className="mb-2">
                <CustomSelectBox
                  label="Select Project"
                  id="projectId"
                  options={projects?.map((project) => ({
                    value: project.id,
                    label: project.name,
                  }))}
                  value={formik.values.projectId}
                  onChange={async (e) => {
                    formik.setFieldValue("workStreamId", "");
                    formik.setFieldValue("projectId", e.target.value);
                    await getWorkStreams(e.target.value);
                  }}
                  fullWidth
                  labelColor="#000"
                  className="w-100"
                  style={{ marginBottom: "8px" }}
                  error={formik.touched.projectId && formik.errors.projectId}
                />
              </div>

              <div className="mb-2">
                <CustomSelectBox
                  label="WorkStream"
                  id="workStreamId"
                  options={workStreams?.data?.map((workStream) => ({
                    value: workStream.id,
                    label: workStream.name,
                  }))}
                  placeholder={
                    workStreams?.data?.length > 0
                      ? "Select WorkStream"
                      : "No WorkStream Found for this project"
                  }
                  value={formik.values.workStreamId}
                  onChange={formik.handleChange}
                  fullWidth
                  labelColor="#000"
                  className="w-100"
                  style={{ marginBottom: "8px" }}
                  error={
                    formik.touched.workStreamId && formik.errors.workStreamId
                  }
                />
              </div>
            </>
          )}

          <div className="d-flex flex-column flex-sm-row gap-3 mb-3">
            <CustomButton
              className="btn-sm px-5 py-2 d-flex items-center justify-center gap-2 flex-1"
              type="submit"
              disabled={loading.abstract}
            >
              {formik.values.abstract ? (
                <span className="d-flex align-items-center gap-2">
                  <TfiReload />
                  <span>
                    {loading.abstract && formik.values.abstract
                      ? "Generating..."
                      : "Regenerate"}
                  </span>
                </span>
              ) : (
                <span>{loading.abstract ? "Processing..." : "Generate"}</span>
              )}
            </CustomButton>
          </div>
        </div>

        <div className="mb-1 mt-4">
          <p className="mb-2 p3">Generate or Paste your full abstract here</p>
          <TextAreaInput
            id="abstract"
            name="abstract"
            value={formik.values.abstract}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Paste your abstract here..."
            rows={4}
            className="w-100 border border-gray-300 rounded p-2"
            style={{ resize: "vertical" }}
            error={formik.touched.abstract && formik.errors.abstract}
          />
        </div>

        {apiError && (
          <div className="alert alert-danger mt-3" role="alert">
            {apiError}
          </div>
        )}

        <p className="text-xs text-gray-500 mb-3">
          For the best results, include your complete abstract with key findings
          & methodology.
        </p>

        <CustomButton
          className="btn-sm py-2 d-flex items-center justify-center gap-2 flex-1"
          onClick={getJournalRecommendations}
          disabled={loading.journals || loading.abstract}
        >
          {loading.journals ? <TfiReload size={18} /> : <TfiReload size={18} />}
          <span>
            {loading.journals
              ? "Finding Journals..."
              : "Find Matching Journals"}
          </span>
        </CustomButton>
      </form>

      {originalJournals.length > 0 && (
        <div className="container mt-4 ">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4">
            <h2 className="h5 m-0">
              Recommended Journals ({filteredJournals.length})
            </h2>

            <div className="d-flex flex-column flex-sm-row gap-3 mt-3 mt-lg-0">
              {/* Subject Area */}
              <div
                style={{ width: "350px" }}
                className="d-flex gap-3 align-items-center"
              >
                <span className="text-nowrap">Subject Area</span>
                <CustomSelectBox
                  id="subject-filter"
                  options={subjectOptions}
                  value={subjectArea}
                  onChange={(e) => setSubjectArea(e.target.value)}
                  fullWidth
                  hideLabel={true}
                  className="p-2"
                  marginBottom="0px"
                />
              </div>

              {/* Sort By */}
              <div className="d-flex gap-3 align-items-center justify-content-center">
                <span className="text-nowrap">Sort By</span>
                <CustomSelectBox
                  id="sort-filter"
                  options={sortOptions}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  fullWidth={false}
                  hideLabel={true}
                  className="p-2"
                  marginBottom="0px"
                />
              </div>

              {/* Filter Reset Button */}
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-light rounded-pill d-flex align-items-center p-2 border"
                  onClick={resetFilters}
                  title="Reset Filters"
                >
                  <MdOutlineFilterAltOff size={21} className="m-0 p-0" />
                </button>
              </div>
            </div>
          </div>

          <div>
            {loading.journals && (
              <div className="text-center mb-4">
                <CustomSkeleton count={3} className="py-5 mb-2" />
              </div>
            )}

            {filteredJournals.length > 0 && (
              <>
                {paginatedJournals.map((journal) => (
                  <div
                    className="col-sm-12 col-md-12 col-lg-10 col-xl-12"
                    key={journal.id}
                  >
                    <JournalCard journal={journal} onRate={handleRate} />
                  </div>
                ))}
                <Pagination
                  totalItems={filteredJournals.length}
                  pageSize={pagination.itemsPerPage}
                  onPageChange={(page) =>
                    setPagination((prev) => ({ ...prev, currentPage: page }))
                  }
                  currentPage={pagination.currentPage}
                />
              </>
            )}

            {!loading.journals &&
              originalJournals.length > 0 &&
              filteredJournals.length === 0 && (
                <div className="alert alert-info">
                  No journals match your current filter criteria. Please try
                  adjusting your filters.
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalRecommender;
